import axios, { type AxiosInstance } from 'axios';
import type {
  EncryptDecipherResponse,
  BacPaymentRequest,
  BacPaymentResponse,
  TransactionGetResponse,
} from '../types';

// URL del backend. Por defecto usa la URL de producción (cuenta raíz CloudFront).
// En desarrollo local, define VITE_API_BASE_URL=/api en tu .env para usar el Vite proxy.
const BASE_URL = import.meta.env.VITE_API_BASE_URL || "https://api.a2csuite.com/cofasa";

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: BASE_URL,
      timeout: 60000,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  /**
   * Descifra un encryptId via GET /encrypt/{hash}.
   * El backend puede devolver:
   *   - Formato directo: {decipherData: "...", createdAt: 123}
   *   - Formato con wrapper: {success: true, body: {decipherData: "...", createdAt: 123}}
   * Normalizamos ambos al formato esperado por el frontend.
   */
  async getEncrypted(hash: string): Promise<EncryptDecipherResponse> {
    const response = await this.client.get(`/encrypt/${encodeURIComponent(hash)}`);
    const data = response.data;

    // Debug: ver qué formato viene
    console.log('[API Debug] BASE_URL:', BASE_URL);
    console.log('[API Debug] getEncrypted response:', response);
    console.log('[API Debug] getEncrypted response.data:', data);

    // Si ya tiene el formato {success, body}, retornarlo tal cual
    if (typeof data === 'object' && data !== null && 'success' in data && 'body' in data) {
      return data as EncryptDecipherResponse;
    }

    // Si viene en formato directo, envolverlo
    if (typeof data === 'object' && data !== null && 'decipherData' in data) {
      return {
        success: true,
        body: {
          decipherData: data.decipherData,
          createdAt: data.createdAt,
        },
      };
    }

    // Fallback: formato inesperado
    throw new Error('Formato de respuesta inesperado del endpoint /encrypt');
  }

  /**
   * POST /payments/bac → procesa el pago BAC (simulado) + acredita en web service COFASA.
   * Normaliza la respuesta para manejar formato directo o con wrapper.
   */
  async postBacPayment(payload: BacPaymentRequest): Promise<BacPaymentResponse> {
    const response = await this.client.post('/payments/bac', payload);
    const data = response.data;

    // Si ya tiene el formato {success, body}, retornarlo tal cual
    if (typeof data === 'object' && data !== null && 'success' in data && 'body' in data) {
      return data as BacPaymentResponse;
    }

    // Preservar errores del backend cuando el envelope omite `body` pero incluye `message`
    // Ej: {success: false, message: 'Tarjeta rechazada por el emisor'}
    if (typeof data === 'object' && data !== null && (data as any).success === false && (data as any).message) {
      return {
        success: false,
        body: { message: (data as any).message } as any,
      };
    }

    // Si viene en formato directo, envolverlo
    if (typeof data === 'object' && data !== null && 'orderId' in data) {
      return {
        success: true,
        body: data,
      };
    }

    // Fallback
    throw new Error('Formato de respuesta inesperado del endpoint /payments/bac');
  }

  /**
   * GET /transaction/{orderId} → lee las transactions del orderId.
   * Normaliza la respuesta para manejar formato directo o con wrapper.
   */
  async getTransactionByOrderId(orderId: string): Promise<TransactionGetResponse> {
    const response = await this.client.get(`/transaction/${encodeURIComponent(orderId)}`);
    const data = response.data;

    // Si ya tiene el formato {success, body}, retornarlo tal cual
    if (typeof data === 'object' && data !== null && 'success' in data && 'body' in data) {
      return data as TransactionGetResponse;
    }

    // Si viene como array directamente, envolverlo
    if (Array.isArray(data)) {
      return {
        success: true,
        body: data,
      };
    }

    // Fallback
    throw new Error('Formato de respuesta inesperado del endpoint /transaction');
  }
}

export const api = new ApiService();
